export interface CountryRanking {
  rank: number;
  code: string; // ISO country code e.g., 'ch', 'sg'
  name: Record<string, string>;
  score: number; // Index score out of 100
  safety: Record<string, string>;
  costLevel: string; // e.g. "$$$$"
  highlight: Record<string, string>;
  category: Record<string, string>;
  metricLabel: Record<string, string>; // e.g. "Quality of Life Index" vs "Attractions Index"
  minCostOfLivingUSD: number; // Minimum cost of living in USD
  internetSpeedMbps?: number; // Only for living
  rentCostUSD?: number; // Only for living
  attractionsCount?: number; // Only for tourism
  comfortVacationCostDailyUSD?: number; // Only for tourism
}

export const TOP_COUNTRIES_LIVING: CountryRanking[] = [
  {
    rank: 1,
    code: "ch",
    name: { en: "Switzerland", ru: "Швейцария" },
    score: 98.6,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$$",
    highlight: { en: "Impeccable infrastructure, alpine nature, medical excellence", ru: "Безупречная инфраструктура, альпийская природа, медицинское превосходство" },
    category: { en: "Premium Living", ru: "Премиум-Жизнь" },
    metricLabel: { en: "Quality of Life: 98.6/100", ru: "Качество жизни: 98.6/100" },
    minCostOfLivingUSD: 3600,
    internetSpeedMbps: 230,
    rentCostUSD: 1950
  },
  {
    rank: 2,
    code: "sg",
    name: { en: "Singapore", ru: "Сингапур" },
    score: 96.8,
    safety: { en: "Ultra High", ru: "Абсолютная" },
    costLevel: "$$$$$",
    highlight: { en: "Global financial powerhouse, zero crime, tropical efficiency", ru: "Мировой финансовый центр, нулевая преступность, тропическая эффективность" },
    category: { en: "Infrastructure", ru: "Инфраструктура" },
    metricLabel: { en: "Quality of Life: 96.8/100", ru: "Качество жизни: 96.8/100" },
    minCostOfLivingUSD: 3800,
    internetSpeedMbps: 260,
    rentCostUSD: 2300
  },
  {
    rank: 3,
    code: "dk",
    name: { en: "Denmark", ru: "Дания" },
    score: 95.4,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Hygge lifestyle, flat bicycle grid, robust social safety", ru: "Философия Хюгге, велосипедные трассы, надежная социальная защита" },
    category: { en: "Social Balance", ru: "Социальный баланс" },
    metricLabel: { en: "Quality of Life: 95.4/100", ru: "Качество жизни: 95.4/100" },
    minCostOfLivingUSD: 2800,
    internetSpeedMbps: 180,
    rentCostUSD: 1400
  },
  {
    rank: 4,
    code: "no",
    name: { en: "Norway", ru: "Норвегия" },
    score: 94.9,
    safety: { en: "Excellent", ru: "Великолепная" },
    costLevel: "$$$$$",
    highlight: { en: "Highest HDI rating, massive sovereign fund, majestic fjords", ru: "Высший индекс ИЧР, суверенный фонд, величественные фьорды" },
    category: { en: "Ecological Oasis", ru: "Экологический оазис" },
    metricLabel: { en: "Quality of Life: 94.9/100", ru: "Качество жизни: 94.9/100" },
    minCostOfLivingUSD: 2900,
    internetSpeedMbps: 190,
    rentCostUSD: 1350
  },
  {
    rank: 5,
    code: "nl",
    name: { en: "Netherlands", ru: "Нидерланды" },
    score: 93.8,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Innovative urban design, liberal culture, tech adaptation", ru: "Инновационный урбанизм, либеральная культура, технологичность" },
    category: { en: "Work-Life Harmony", ru: "Гармония жизни и работы" },
    metricLabel: { en: "Quality of Life: 93.8/100", ru: "Качество жизни: 93.8/100" },
    minCostOfLivingUSD: 2700,
    internetSpeedMbps: 175,
    rentCostUSD: 1550
  },
  {
    rank: 6,
    code: "fi",
    name: { en: "Finland", ru: "Финляндия" },
    score: 93.1,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Ranked world's happiest country, pure lakes, northern lights", ru: "Самая счастливая страна мира, чистейшие озера, северное сияние" },
    category: { en: "Happiness Index", ru: "Индекс Счастья" },
    metricLabel: { en: "Quality of Life: 93.1/100", ru: "Качество жизни: 93.1/100" },
    minCostOfLivingUSD: 2400,
    internetSpeedMbps: 150,
    rentCostUSD: 1050
  },
  {
    rank: 7,
    code: "de",
    name: { en: "Germany", ru: "Германия" },
    score: 91.5,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Strong economic anchor, outstanding healthcare, historic cities", ru: "Главная экономика ЕС, передовая медицина, исторические города" },
    category: { en: "Economic Anchor", ru: "Экономическая стабильность" },
    metricLabel: { en: "Quality of Life: 91.5/100", ru: "Качество жизни: 91.5/100" },
    minCostOfLivingUSD: 2300,
    internetSpeedMbps: 140,
    rentCostUSD: 1100
  },
  {
    rank: 8,
    code: "au",
    name: { en: "Australia", ru: "Австралия" },
    score: 90.7,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Perfect sunny beaches, highly active outdoor coastal culture", ru: "Великолепные солнечные пляжи, активный спорт, океанский воздух" },
    category: { en: "Coastal Paradise", ru: "Океанский стиль жизни" },
    metricLabel: { en: "Quality of Life: 90.7/100", ru: "Качество жизни: 90.7/100" },
    minCostOfLivingUSD: 2800,
    internetSpeedMbps: 115,
    rentCostUSD: 1450
  },
  {
    rank: 9,
    code: "ca",
    name: { en: "Canada", ru: "Канада" },
    score: 89.9,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Multicultural tolerance, pristine nature, vast forest expanses", ru: "Мультикультурный эталон, чистейшие заповедники, лесные масштабы" },
    category: { en: "Multicultural Hub", ru: "Дружелюбный хаб" },
    metricLabel: { en: "Quality of Life: 89.9/100", ru: "Качество жизни: 89.9/100" },
    minCostOfLivingUSD: 2500,
    internetSpeedMbps: 165,
    rentCostUSD: 1300
  },
  {
    rank: 10,
    code: "se",
    name: { en: "Sweden", ru: "Швеция" },
    score: 89.4,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Eco-innovation leader, modern social security, startup success", ru: "Лидер эко-технологий, прогрессивная соцподдержка, стартап-хаб" },
    category: { en: "Tech & Innovation", ru: "Технологии и Экология" },
    metricLabel: { en: "Quality of Life: 89.4/100", ru: "Качество жизни: 89.4/100" },
    minCostOfLivingUSD: 2450,
    internetSpeedMbps: 170,
    rentCostUSD: 1100
  },
  {
    rank: 11,
    code: "nz",
    name: { en: "New Zealand", ru: "Новая Зеландия" },
    score: 88.8,
    safety: { en: "Excellent", ru: "Великолепная" },
    costLevel: "$$$$",
    highlight: { en: "Secluded pristine sanctuary, stunning fjords, extreme sports", ru: "Уединенный природный заповедник, величественные фьорды, спорт" },
    category: { en: "Remote Sanctuary", ru: "Уединенный заповедник" },
    metricLabel: { en: "Quality of Life: 88.8/100", ru: "Качество жизни: 88.8/100" },
    minCostOfLivingUSD: 2600,
    internetSpeedMbps: 145,
    rentCostUSD: 1250
  },
  {
    rank: 12,
    code: "at",
    name: { en: "Austria", ru: "Австрия" },
    score: 88.3,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Imperial architectures of Vienna, alpine lakes, classical soundscapes", ru: "Императорские дворцы Вены, горные озера, классическая музыка" },
    category: { en: "Heritage & Art", ru: "Наследие и Культура" },
    metricLabel: { en: "Quality of Life: 88.3/100", ru: "Качество жизни: 88.3/100" },
    minCostOfLivingUSD: 2350,
    internetSpeedMbps: 135,
    rentCostUSD: 1100
  },
  {
    rank: 13,
    code: "jp",
    name: { en: "Japan", ru: "Япония" },
    score: 87.9,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Incredible public hygiene, absolute safety, culinary precision", ru: "Идеальная чистота улиц, абсолютная безопасность, шедевры суши" },
    category: { en: "Cultural Harmony", ru: "Традиции и Порядок" },
    metricLabel: { en: "Quality of Life: 87.9/100", ru: "Качество жизни: 87.9/100" },
    minCostOfLivingUSD: 1800,
    internetSpeedMbps: 185,
    rentCostUSD: 900
  },
  {
    rank: 14,
    code: "es",
    name: { en: "Spain", ru: "Испания" },
    score: 86.5,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$",
    highlight: { en: "Mediterranean climate, tapas culture, highly active night streets", ru: "Мягкий климат, тапас, развитая инфраструктура, душевный ритм" },
    category: { en: "Mediterranean Soul", ru: "Средиземноморский ритм" },
    metricLabel: { en: "Quality of Life: 86.5/100", ru: "Качество жизни: 86.5/100" },
    minCostOfLivingUSD: 1700,
    internetSpeedMbps: 195,
    rentCostUSD: 850
  },
  {
    rank: 15,
    code: "pt",
    name: { en: "Portugal", ru: "Португалия" },
    score: 85.2,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$",
    highlight: { en: "Warm sunny coastlines, massive digital nomad presence, high tax breaks", ru: "Солнечное океанское побережье, рай для диджитал-номадов, налоги" },
    category: { en: "Digital Nomad Oasis", ru: "Рай для фрилансеров" },
    metricLabel: { en: "Quality of Life: 85.2/100", ru: "Качество жизни: 85.2/100" },
    minCostOfLivingUSD: 1400,
    internetSpeedMbps: 160,
    rentCostUSD: 750
  },
  {
    rank: 16,
    code: "gb",
    name: { en: "United Kingdom", ru: "Великобритания" },
    score: 84.1,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$$$",
    highlight: { en: "Financial heart in London, rich universities, global language leadership", ru: "Финансовое сердце Лондона, вековые университеты, английский язык" },
    category: { en: "Global Influence", ru: "Глобальное влияние" },
    metricLabel: { en: "Quality of Life: 84.1/100", ru: "Качество жизни: 84.1/100" },
    minCostOfLivingUSD: 2500,
    internetSpeedMbps: 120,
    rentCostUSD: 1400
  },
  {
    rank: 17,
    code: "fr",
    name: { en: "France", ru: "Франция" },
    score: 83.6,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$$$",
    highlight: { en: "Art world capital, exquisite wine yards, rich worker protections", ru: "Мировая столица искусств, изысканные шато, защита прав трудящихся" },
    category: { en: "Culinary & Intellect", ru: "Кулинария и Эстетика" },
    metricLabel: { en: "Quality of Life: 83.6/100", ru: "Качество жизни: 83.6/100" },
    minCostOfLivingUSD: 2200,
    internetSpeedMbps: 170,
    rentCostUSD: 1150
  },
  {
    rank: 18,
    code: "it",
    name: { en: "Italy", ru: "Италия" },
    score: 82.9,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$",
    highlight: { en: "Dolce Vita, unparalleled cultural ruins, superb culinary arts", ru: "Дольче вита, древнейшие руины цивилизации, великая кухня" },
    category: { en: "Dolce Vita", ru: "Красивая жизнь" },
    metricLabel: { en: "Quality of Life: 82.9/100", ru: "Качество жизни: 82.9/100" },
    minCostOfLivingUSD: 1950,
    internetSpeedMbps: 130,
    rentCostUSD: 950
  },
  {
    rank: 19,
    code: "ae",
    name: { en: "United Arab Emirates", ru: "ОАЭ" },
    score: 82.1,
    safety: { en: "Ultra High", ru: "Абсолютная" },
    costLevel: "$$$$$",
    highlight: { en: "Futuristic skyscrapers, zero personal tax, hyper-modern safety", ru: "Футуристические оазисы, нулевой подоходный налог, безопасность" },
    category: { en: "Futuristic Megacity", ru: "Мегаполис Будущего" },
    metricLabel: { en: "Quality of Life: 82.1/100", ru: "Качество жизни: 82.1/100" },
    minCostOfLivingUSD: 3100,
    internetSpeedMbps: 200,
    rentCostUSD: 1800
  },
  {
    rank: 20,
    code: "kr",
    name: { en: "South Korea", ru: "Южная Корея" },
    score: 81.3,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Hyper-fast fiber internet, high tech lifestyle, neon cities", ru: "Сверхскоростной интернет, культ технологий, круглосуточные города" },
    category: { en: "Hyper-Connectivity", ru: "Мир Высоких Технологий" },
    metricLabel: { en: "Quality of Life: 81.3/100", ru: "Качество жизни: 81.3/100" },
    minCostOfLivingUSD: 1900,
    internetSpeedMbps: 210,
    rentCostUSD: 850
  },
  {
    rank: 21,
    code: "is",
    name: { en: "Iceland", ru: "Исландия" },
    score: 80.8,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$$",
    highlight: { en: "Volcanic geothermal energy, absolute social equality, zero military", ru: "Геотермальные бассейны, полное социальное равенство, мирная гавань" },
    category: { en: "Nordic Freedom", ru: "Северная свобода" },
    metricLabel: { en: "Quality of Life: 80.8/100", ru: "Качество жизни: 80.8/100" },
    minCostOfLivingUSD: 2650,
    internetSpeedMbps: 190,
    rentCostUSD: 1500
  },
  {
    rank: 22,
    code: "ie",
    name: { en: "Ireland", ru: "Ирландия" },
    score: 79.5,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Corporate tech hub of Europe, green scenic hills, warm pub culture", ru: "Корпоративное сердце Европы, изумрудные холмы, радушные пабы" },
    category: { en: "Sovereign Tech", ru: "Корпоративный Олимп" },
    metricLabel: { en: "Quality of Life: 79.5/100", ru: "Качество жизни: 79.5/100" },
    minCostOfLivingUSD: 2700,
    internetSpeedMbps: 130,
    rentCostUSD: 1700
  },
  {
    rank: 23,
    code: "cy",
    name: { en: "Cyprus", ru: "Кипр" },
    score: 78.4,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$",
    highlight: { en: "Massive sun count, low corporate tax, cozy Mediterranean villas", ru: "320 солнечных дней в году, низкие налоги, комфортные виллы" },
    category: { en: "Island Sanctuary", ru: "Остров Храбрости" },
    metricLabel: { en: "Quality of Life: 78.4/100", ru: "Качество жизни: 78.4/100" },
    minCostOfLivingUSD: 1800,
    internetSpeedMbps: 95,
    rentCostUSD: 1000
  },
  {
    rank: 24,
    code: "th",
    name: { en: "Thailand", ru: "Таиланд" },
    score: 77.2,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Superb value for money, majestic beaches, world-renowned massage", ru: "Лучшее соотношение цены/качества, тропики, знаменитый массаж" },
    category: { en: "Tropic Haven", ru: "Тропический рай" },
    metricLabel: { en: "Quality of Life: 77.2/100", ru: "Качество жизни: 77.2/100" },
    minCostOfLivingUSD: 1100,
    internetSpeedMbps: 220,
    rentCostUSD: 500
  },
  {
    rank: 25,
    code: "us",
    name: { en: "United States", ru: "США" },
    score: 76.9,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$$$$",
    highlight: { en: "Silicon Valley startups, ultimate diversity, enormous national scale", ru: "Стартапы Кремниевой долины, неограниченные возможности, масштаб" },
    category: { en: "Vast Horizons", ru: "Новые Горизонты" },
    metricLabel: { en: "Quality of Life: 76.9/100", ru: "Качество жизни: 76.9/100" },
    minCostOfLivingUSD: 3300,
    internetSpeedMbps: 195,
    rentCostUSD: 1750
  },
  {
    rank: 26,
    code: "tw",
    name: { en: "Taiwan", ru: "Тайвань" },
    score: 75.8,
    safety: { en: "Ultra High", ru: "Абсолютная" },
    costLevel: "$$",
    highlight: { en: "Outstanding health care system, friendly expats, vibrant night markets", ru: "Передовая система медицины, доброжелательность к экспатам, ночные рынки" },
    category: { en: "Subtropical Comfort", ru: "Субтропический Уют" },
    metricLabel: { en: "Quality of Life: 75.8/100", ru: "Качество жизни: 75.8/100" },
    minCostOfLivingUSD: 1300,
    internetSpeedMbps: 180,
    rentCostUSD: 550
  },
  {
    rank: 27,
    code: "cz",
    name: { en: "Czech Republic", ru: "Чехия" },
    score: 74.5,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$",
    highlight: { en: "Beautiful historic heart of Europe, robust architecture, and safe environment", ru: "Живописная архитектура в сердце Европы, развитая культура и высокий уровень безопасности" },
    category: { en: "Bohemian Legacy", ru: "Богемское Наследие" },
    metricLabel: { en: "Quality of Life: 74.5/100", ru: "Качество жизни: 74.5/100" },
    minCostOfLivingUSD: 1600,
    internetSpeedMbps: 110,
    rentCostUSD: 800
  }
];

export const TOP_COUNTRIES_TOURISM: CountryRanking[] = [
  {
    rank: 1,
    code: "fr",
    name: { en: "France", ru: "Франция" },
    score: 99.5,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$$$",
    highlight: { en: "Most visited country worldwide, world-class landmarks, Versailles", ru: "Самая посещаемая страна мира, Эйфелева башня, Версаль, Лувр" },
    category: { en: "Gourmet & History", ru: "Гастрономия и Историзм" },
    metricLabel: { en: "Attractions Score: 99.5/100", ru: "Рейтинг достопримечательностей: 99.5/100" },
    minCostOfLivingUSD: 2200,
    attractionsCount: 220,
    comfortVacationCostDailyUSD: 160
  },
  {
    rank: 2,
    code: "it",
    name: { en: "Italy", ru: "Италия" },
    score: 98.9,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$",
    highlight: { en: "Most UNESCO sites globally, Venice canals, Colosseum, Amalfi", ru: "Рекордсмен по объектам ЮНЕСКО, каналы Венеции, Колизей, Амальфи" },
    category: { en: "Art & Antiquities", ru: "Искусство и Руины" },
    metricLabel: { en: "Attractions Score: 98.9/100", ru: "Рейтинг достопримечательностей: 98.9/100" },
    minCostOfLivingUSD: 1950,
    attractionsCount: 280,
    comfortVacationCostDailyUSD: 150
  },
  {
    rank: 3,
    code: "es",
    name: { en: "Spain", ru: "Испания" },
    score: 97.4,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$",
    highlight: { en: "Ibiza sands, Sagrada Familia art, high summer festivals", ru: "Пляжи Ибицы, соборы Гауди в Барселоне, бесконечная фиеста" },
    category: { en: "Endless Sunshine", ru: "Бесконечное Солнце" },
    metricLabel: { en: "Attractions Score: 97.4/100", ru: "Рейтинг достопримечательностей: 97.4/100" },
    minCostOfLivingUSD: 1700,
    attractionsCount: 210,
    comfortVacationCostDailyUSD: 130
  },
  {
    rank: 4,
    code: "us",
    name: { en: "United States", ru: "США" },
    score: 96.1,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$$$$",
    highlight: { en: "Grand Canyon marvels, Manhattan skylines, National Parks", ru: "Великий каньон, огни Таймс-сквер, Диснейленд, парки" },
    category: { en: "Scenic Frontiers", ru: "Масштабный туризм" },
    metricLabel: { en: "Attractions Score: 96.1/100", ru: "Рейтинг достопримечательностей: 96.1/100" },
    minCostOfLivingUSD: 3300,
    attractionsCount: 240,
    comfortVacationCostDailyUSD: 210
  },
  {
    rank: 5,
    code: "jp",
    name: { en: "Japan", ru: "Япония" },
    score: 95.8,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Kyoto shrines, Mount Fuji views, futuristic Tokyo nights", ru: "Храмы Киото, священная Фудзи, неоновый Токио, онсены" },
    category: { en: "Harmonious Contrasts", ru: "Гармония Контрастов" },
    metricLabel: { en: "Attractions Score: 95.8/100", ru: "Рейтинг достопримечательностей: 95.8/100" },
    minCostOfLivingUSD: 1800,
    attractionsCount: 190,
    comfortVacationCostDailyUSD: 165
  },
  {
    rank: 6,
    code: "gr",
    name: { en: "Greece", ru: "Греция" },
    score: 94.2,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$",
    highlight: { en: "Santorini sunsets, Parthenon temple, blue Aegean crystal water", ru: "Закаты на Санторини, Парфенон, лазурные бухты Эгейского моря" },
    category: { en: "Ancient Mythologies", ru: "Античные мифы" },
    metricLabel: { en: "Attractions Score: 94.2/100", ru: "Рейтинг достопримечательностей: 94.2/100" },
    minCostOfLivingUSD: 1550,
    attractionsCount: 175,
    comfortVacationCostDailyUSD: 110
  },
  {
    rank: 7,
    code: "th",
    name: { en: "Thailand", ru: "Таиланд" },
    score: 93.5,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Golden Buddha shrines, Phuket islands, ultimate night markets", ru: "Золотые буддийские храмы, острова Пхукета, шумные рынки" },
    category: { en: "Far-East Adventure", ru: "Дальневосточная Экзотика" },
    metricLabel: { en: "Attractions Score: 93.5/100", ru: "Рейтинг достопримечательностей: 93.5/100" },
    minCostOfLivingUSD: 1100,
    attractionsCount: 150,
    comfortVacationCostDailyUSD: 70
  },
  {
    rank: 8,
    code: "mx",
    name: { en: "Mexico", ru: "Мексика" },
    score: 92.1,
    safety: { en: "Low-Moderate", ru: "Умеренно-низкая" },
    costLevel: "$$",
    highlight: { en: "Chichen Itza pyramids, Tulum cliffs, world-famous tacos, Mezcal", ru: "Пирамиды Чичен-Ица, пляжи Тулума, мексиканские карнавалы" },
    category: { en: "Ancient Civilizations", ru: "Древние Цивилизации" },
    metricLabel: { en: "Attractions Score: 92.1/100", ru: "Рейтинг достопримечательностей: 92.1/100" },
    minCostOfLivingUSD: 1350,
    attractionsCount: 160,
    comfortVacationCostDailyUSD: 90
  },
  {
    rank: 9,
    code: "gb",
    name: { en: "United Kingdom", ru: "Великобритания" },
    score: 91.4,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Big Ben clock tower, Scottish highlands, Stonehenge mystics", ru: "Башня Биг-Бен, замки Шотландии, мистический Стоунхендж" },
    category: { en: "Royal Traditions", ru: "Королевские Традиции" },
    metricLabel: { en: "Attractions Score: 91.4/100", ru: "Рейтинг достопримечательностей: 91.4/100" },
    minCostOfLivingUSD: 2500,
    attractionsCount: 180,
    comfortVacationCostDailyUSD: 180
  },
  {
    rank: 10,
    code: "de",
    name: { en: "Germany", ru: "Германия" },
    score: 90.3,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$$$",
    highlight: { en: "Fairytale Neuschwanstein castle, vast Rhine river cruises", ru: "Сказочный замок Нойшванштайн, круизы по Рейну, пивные фестивали" },
    category: { en: "Châteaux & Lore", ru: "Замки и Сказания" },
    metricLabel: { en: "Attractions Score: 90.3/100", ru: "Рейтинг достопримечательностей: 90.3/100" },
    minCostOfLivingUSD: 2300,
    attractionsCount: 165,
    comfortVacationCostDailyUSD: 140
  },
  {
    rank: 11,
    code: "tr",
    name: { en: "Turkey", ru: "Турция" },
    score: 89.8,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Cappadocia colorful balloons, Grand Bazaar, Hagia Sophia", ru: "Воздушные шары Каппадокии, мечеть Айя-София, бирюзовый берег" },
    category: { en: "Silk & Spices", ru: "Шелк и Пряности" },
    metricLabel: { en: "Attractions Score: 89.8/100", ru: "Рейтинг достопримечательностей: 89.8/100" },
    minCostOfLivingUSD: 1150,
    attractionsCount: 180,
    comfortVacationCostDailyUSD: 85
  },
  {
    rank: 12,
    code: "pt",
    name: { en: "Portugal", ru: "Португалия" },
    score: 88.5,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$",
    highlight: { en: "Lisbon manual trams, Sintra palaces, enormous Atlantic surf waves", ru: "Желтые трамваи Лиссабона, замки Синтры, волны Назаре для серфинга" },
    category: { en: "Atlantic Explorers", ru: "Океанские Гавани" },
    metricLabel: { en: "Attractions Score: 88.5/100", ru: "Рейтинг достопримечательностей: 88.5/100" },
    minCostOfLivingUSD: 1400,
    attractionsCount: 115,
    comfortVacationCostDailyUSD: 110
  },
  {
    rank: 13,
    code: "ch",
    name: { en: "Switzerland", ru: "Швейцария" },
    score: 87.2,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$$",
    highlight: { en: "Zermatt Matterhorn peaks, turquoise lakes, world's best trains", ru: "Вершина Маттерхорн, горные панорамы, лучшие панорамные поезда" },
    category: { en: "Alpine Majesty", ru: "Альпийские Панорамы" },
    metricLabel: { en: "Attractions Score: 87.2/100", ru: "Рейтинг достопримечательностей: 87.2/100" },
    minCostOfLivingUSD: 3600,
    attractionsCount: 140,
    comfortVacationCostDailyUSD: 240
  },
  {
    rank: 14,
    code: "at",
    name: { en: "Austria", ru: "Австрия" },
    score: 86.4,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Habsburg empire palaces, Mozart museum, alpine ski slopes", ru: "Дворцы Габсбургов, дом-музей Моцарта, горнолыжные трассы" },
    category: { en: "Classic Echoes", ru: "Классическая Эпоха" },
    metricLabel: { en: "Attractions Score: 86.4/100", ru: "Рейтинг достопримечательностей: 86.4/100" },
    minCostOfLivingUSD: 2350,
    attractionsCount: 120,
    comfortVacationCostDailyUSD: 135
  },
  {
    rank: 15,
    code: "za",
    name: { en: "South Africa", ru: "Южная Африка" },
    score: 85.9,
    safety: { en: "Low", ru: "Низкая" },
    costLevel: "$$$",
    highlight: { en: "Kruger wildlife safaris, Table Mountain slopes, scenic garden routes", ru: "Сафари с большой пятеркой в Крюгере, пингвины в Кейптауне" },
    category: { en: "Savannah & Safari", ru: "Дикая Саванна и Сафари" },
    metricLabel: { en: "Attractions Score: 85.9/100", ru: "Рейтинг достопримечательностей: 85.9/100" },
    minCostOfLivingUSD: 1200,
    attractionsCount: 110,
    comfortVacationCostDailyUSD: 85
  },
  {
    rank: 16,
    code: "vn",
    name: { en: "Vietnam", ru: "Вьетнам" },
    score: 84.8,
    safety: { en: "High", ru: "Высокая" },
    costLevel: "$$",
    highlight: { en: "Ha Long Bay emerald pillars, historic lantern town of Hoi An", ru: "Залив Халонг с тысячами скал, бумажные фонарики Хойана" },
    category: { en: "Southeast Wonders", ru: "Юго-Восточное Очарование" },
    metricLabel: { en: "Attractions Score: 84.8/100", ru: "Рейтинг достопримечательностей: 84.8/100" },
    minCostOfLivingUSD: 950,
    attractionsCount: 120,
    comfortVacationCostDailyUSD: 60
  },
  {
    rank: 17,
    code: "ca",
    name: { en: "Canada", ru: "Канада" },
    score: 83.9,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Breathtaking Banff turquoise waters, roaring Niagara falls", ru: "Лазурные озера Банфа в Скалистых горах, Ниагарский водопад" },
    category: { en: "Untamed North", ru: "Дикий Север" },
    metricLabel: { en: "Attractions Score: 83.9/100", ru: "Рейтинг достопримечательностей: 83.9/100" },
    minCostOfLivingUSD: 2500,
    attractionsCount: 135,
    comfortVacationCostDailyUSD: 155
  },
  {
    rank: 18,
    code: "au",
    name: { en: "Australia", ru: "Австралия" },
    score: 82.5,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Great Barrier Reef scuba, iconic Sydney Opera harbor", ru: "Большой Барьерный риф, кенгуру и коалы, Порт-Джексон" },
    category: { en: "Southern Reefs", ru: "Южные Рифы" },
    metricLabel: { en: "Attractions Score: 82.5/100", ru: "Рейтинг достопримечательностей: 82.5/100" },
    minCostOfLivingUSD: 2800,
    attractionsCount: 140,
    comfortVacationCostDailyUSD: 170
  },
  {
    rank: 19,
    code: "nz",
    name: { en: "New Zealand", ru: "Новая Зеландия" },
    score: 81.9,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$",
    highlight: { en: "Hobbiton village hills, thermal geysers at Rotorua", ru: "Холмы Хоббитона из Властелина колец, гейзеры Роторуа" },
    category: { en: "Middle-Earth Lore", ru: "Вдохновение Толкина" },
    metricLabel: { en: "Attractions Score: 81.9/100", ru: "Рейтинг достопримечательностей: 81.9/100" },
    minCostOfLivingUSD: 2600,
    attractionsCount: 100,
    comfortVacationCostDailyUSD: 160
  },
  {
    rank: 20,
    code: "br",
    name: { en: "Brazil", ru: "Бразилия" },
    score: 80.5,
    safety: { en: "Low-Moderate", ru: "Умеренно-низкая" },
    costLevel: "$$$",
    highlight: { en: "Rio Carnival feathers, Giant Redeemer statue, Amazon cruises", ru: "Карнавал в Рио, монументальный Христос-Искупитель, Амазонка" },
    category: { en: "Amplitudes of Samba", ru: "Ритмы Самбы" },
    metricLabel: { en: "Attractions Score: 80.5/100", ru: "Рейтинг достопримечательностей: 80.5/100" },
    minCostOfLivingUSD: 1100,
    attractionsCount: 115,
    comfortVacationCostDailyUSD: 95
  },
  {
    rank: 21,
    code: "is",
    name: { en: "Iceland", ru: "Исландия" },
    score: 79.4,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$$",
    highlight: { en: "Steam-filled Blue Lagoon, colossal black beaches of Vik", ru: "Пар геотермального курорта Голубая лагуна, черные пляжи Вика" },
    category: { en: "Ice & Volcanoes", ru: "Лед и Пламя" },
    metricLabel: { en: "Attractions Score: 79.4/100", ru: "Рейтинг достопримечательностей: 79.4/100" },
    minCostOfLivingUSD: 2650,
    attractionsCount: 95,
    comfortVacationCostDailyUSD: 220
  },
  {
    rank: 22,
    code: "pe",
    name: { en: "Peru", ru: "Перу" },
    score: 78.1,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Mountain citadel Machu Picchu, high elevation Titicaca lake", ru: "Затерянный город инков Мачу-Пикчу, высокогорное озеро Титикака" },
    category: { en: "Lost Empires", ru: "Потерянные Империи" },
    metricLabel: { en: "Attractions Score: 78.1/100", ru: "Рейтинг достопримечательностей: 78.1/100" },
    minCostOfLivingUSD: 1000,
    attractionsCount: 105,
    comfortVacationCostDailyUSD: 70
  },
  {
    rank: 23,
    code: "sg",
    name: { en: "Singapore", ru: "Сингапур" },
    score: 77.5,
    safety: { en: "Outstanding", ru: "Исключительная" },
    costLevel: "$$$$$",
    highlight: { en: "Gardens by the Bay super trees, world's leading dynamic zoo", ru: "Сверхвысокие деревья Gardens by the Bay, футуристический дизайн" },
    category: { en: "Architectural Gems", ru: "Архитектура будущего" },
    metricLabel: { en: "Attractions Score: 77.5/100", ru: "Рейтинг достопримечательностей: 77.5/100" },
    minCostOfLivingUSD: 3800,
    attractionsCount: 90,
    comfortVacationCostDailyUSD: 195
  },
  {
    rank: 24,
    code: "ma",
    name: { en: "Morocco", ru: "Марокко" },
    score: 76.8,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Marrakech blue botanical arrays, Chefchaouen indigo walls", ru: "Караваны в Сахаре, средневековые базары Марракеша" },
    category: { en: "Sands & Souks", ru: "Пески и Восточные Базары" },
    metricLabel: { en: "Attractions Score: 76.8/100", ru: "Рейтинг достопримечательностей: 76.8/100" },
    minCostOfLivingUSD: 1050,
    attractionsCount: 110,
    comfortVacationCostDailyUSD: 70
  },
  {
    rank: 25,
    code: "eg",
    name: { en: "Egypt", ru: "Египет" },
    score: 76.1,
    safety: { en: "Moderate", ru: "Умеренная" },
    costLevel: "$$",
    highlight: { en: "Giza great Pyramids, Valley of Kings tombs, Nile luxury cruises", ru: "Великие пирамиды Гизы, гробницы в Долине царей, круизы по Нилу" },
    category: { en: "Dawn of Time", ru: "Колыбель Цивилизаций" },
    metricLabel: { en: "Attractions Score: 76.1/100", ru: "Рейтинг достопримечательностей: 76.1/100" },
    minCostOfLivingUSD: 900,
    attractionsCount: 130,
    comfortVacationCostDailyUSD: 75
  },
  {
    rank: 26,
    code: "ae",
    name: { en: "United Arab Emirates", ru: "ОАЭ" },
    score: 75.8,
    safety: { en: "Ultra High", ru: "Абсолютная" },
    costLevel: "$$$$$",
    highlight: { en: "Burj Khalifa wonder, Palm Jumeirah islands, rich cultural experience", ru: "Величественная Бурдж-Халифа, насыпные острова Пальма Джумейра, премиум шопинг" },
    category: { en: "Oasis of Luxury", ru: "Оазис Роскоши" },
    metricLabel: { en: "Attractions Score: 75.8/100", ru: "Рейтинг достопримечательностей: 75.8/100" },
    minCostOfLivingUSD: 3100,
    attractionsCount: 125,
    comfortVacationCostDailyUSD: 185
  },
  {
    rank: 27,
    code: "hr",
    name: { en: "Croatia", ru: "Хорватия" },
    score: 74.9,
    safety: { en: "Very High", ru: "Очень Высокая" },
    costLevel: "$$$",
    highlight: { en: "Stunning Adriatic coast, dry-stone historic walled towns, Plitvice Lakes", ru: "Великолепное Адриатическое побережье, Дубровник, Плитвицкие озера" },
    category: { en: "Adriatic Pearl", ru: "Адриатическая Жемчужина" },
    metricLabel: { en: "Attractions Score: 74.9/100", ru: "Рейтинг достопримечательностей: 74.9/100" },
    minCostOfLivingUSD: 1600,
    attractionsCount: 105,
    comfortVacationCostDailyUSD: 115
  }
];
